import { useState, useEffect, useMemo } from "react";
import {
  TrendingUp, TrendingDown, Plus, Trash2, Pencil, Search,
  Receipt, ArrowUpCircle, ArrowDownCircle, Settings2, X, Check,
  CreditCard, BarChart3, Calculator, BookOpen, Download,
  HelpCircle, AlertTriangle, Info, ChevronDown, ChevronUp, CalendarDays,
  Paperclip, FileText, Image as ImageIcon, File as FileIcon, RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import { Input, Card, Badge, Button, Modal, Textarea } from "@/components/common";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useF1Help } from "@/lib/useF1Help";
import { TestMarke } from "@/components/TestMarke";

// ==================== MAIN PAGE ====================
const BuchhaltungPage = () => {
  useF1Help("hilfe_buchhaltung");
  // ?tab=posten (z. B. aus der AB "Zur Buchhaltung")
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "uebersicht");
  const [buchungen, setBuchungen] = useState([]);
  const [stats, setStats] = useState(null);
  const [offenePosten, setOffenePosten] = useState([]);
  const [kategorien, setKategorien] = useState({ einnahme: [], ausgabe: [] });
  const [loading, setLoading] = useState(true);
  const [zeitraum, setZeitraum] = useState("jahr");
  const [typFilter, setTypFilter] = useState("alle");
  const [search, setSearch] = useState("");
  const [editBuchung, setEditBuchung] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [showKategorien, setShowKategorien] = useState(false);
  const [showHilfe, setShowHilfe] = useState(false);

  useEffect(() => { loadAll(); }, [zeitraum, typFilter]);

  const loadAll = async () => {
    try {
      const [buchRes, statsRes, postenRes, katRes] = await Promise.all([
        api.get("/buchhaltung/buchungen", { params: { zeitraum, typ: typFilter } }),
        api.get("/buchhaltung/statistiken", { params: { zeitraum } }),
        api.get("/buchhaltung/offene-posten"),
        api.get("/buchhaltung/kategorien"),
      ]);
      setBuchungen(buchRes.data);
      setStats(statsRes.data);
      setOffenePosten(postenRes.data);
      setKategorien(katRes.data);
    } catch {
      toast.error("Fehler beim Laden der Buchhaltungsdaten");
    } finally { setLoading(false); }
  };

  // 07.10.2026: "Rückgängig" storniert per Gegenbuchung (nichts wird gelöscht)
  const handleDeleteBuchung = async (id) => {
    try {
      const r = await api.delete(`/buchhaltung/buchungen/${id}`);
      toast.success(r.data?.message || "Buchung storniert");
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Fehler beim Zurücknehmen"); }
  };

  const handleUploadBeleg = async (buchungId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/buchhaltung/buchungen/${buchungId}/belege`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Beleg "${file.name}" hochgeladen`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Upload");
    }
  };

  const handleDownloadBeleg = async (beleg) => {
    try {
      const res = await api.get(`/buchhaltung/belege/${beleg.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = beleg.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Fehler beim Herunterladen");
    }
  };

  const handleDeleteBeleg = async (buchungId, belegId, name) => {
    // 07.10.2026: nur Admin, mit Rueckfrage
    if (!window.confirm(`Beleg „${name || ""}" wirklich löschen?`)) return;
    try {
      await api.delete(`/buchhaltung/buchungen/${buchungId}/belege/${belegId}`);
      toast.success("Beleg gelöscht");
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Fehler beim Löschen"); }
  };

  const handleUndoPayment = async (buchung) => {
    // 06.10.2026: Buchungen aus "Zahlung buchen" einzeln zuruecknehmen
    if (buchung?.zahlung_id) {
      try {
        const r = await api.post(`/buchhaltung/zahlung-rueckgaengig-buchung/${buchung.id}`);
        toast.success(`Buchung zurückgenommen (Storno ${r.data?.gegenbuchung?.belegnummer || ""})`);
        loadAll();
      } catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
      return;
    }
    const invoiceId = buchung?.rechnung_id || buchung;
    try {
      await api.post(`/buchhaltung/zahlung-rueckgaengig/${invoiceId}`);
      toast.success("Zahlung rückgängig gemacht");
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };

  const handleExportCSV = async () => {
    try {
      const res = await api.get("/buchhaltung/export-csv", { params: { zeitraum }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Buchhaltung_${zeitraum}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV-Export heruntergeladen");
    } catch {
      toast.error("Fehler beim CSV-Export");
    }
  };

  const filtered = useMemo(() => {
    if (!search) return buchungen;
    const s = search.toLowerCase();
    return buchungen.filter(
      (b) =>
        (b.beschreibung || "").toLowerCase().includes(s) ||
        (b.kategorie || "").toLowerCase().includes(s) ||
        (b.kunde || "").toLowerCase().includes(s) ||
        (b.rechnung_nr || "").toLowerCase().includes(s) ||
        (b.belegnummer || "").toLowerCase().includes(s)
    );
  }, [buchungen, search]);

  const zeitraumLabels = { monat: "Monat", quartal: "Quartal", jahr: "Jahr", alle: "Gesamt" };

  const tabs = [
    { key: "uebersicht", label: "Übersicht", icon: BarChart3 },
    { key: "buchungen", label: "Buchungen", icon: Receipt },
    { key: "kassenbuch", label: "Kassenbuch", icon: BookOpen },
    { key: "posten", label: "Offene Posten", icon: CreditCard },
    { key: "monatsabschluss", label: "Monatsabschluss", icon: CalendarDays },
    { key: "ust", label: "USt/MwSt", icon: Calculator },
    { key: "uebung", label: "Übung", icon: RotateCcw },  // A-024: Testkunden/Übungsbelege – zählt nirgends mit
  ];

  return (
    <div data-testid="buchhaltung-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl lg:text-4xl font-bold">Buchhaltung</h1>
          <p className="text-muted-foreground mt-1 text-sm lg:text-base">Einnahmen, Ausgaben & Finanzen verwalten</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowHilfe(true)} data-testid="btn-hilfe">
            <HelpCircle className="w-4 h-4 mr-1.5" /> Hilfe
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="btn-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> CSV-Export
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowKategorien(true)} data-testid="btn-kategorien">
            <Settings2 className="w-4 h-4 mr-1.5" /> Kategorien
          </Button>
          <Button onClick={() => setShowNewForm(true)} data-testid="btn-neue-buchung">
            <Plus className="w-4 h-4 mr-1.5" /> Neue Buchung
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b overflow-x-auto" data-testid="buchhaltung-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
              tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Zeitraum Filter */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["monat", "quartal", "jahr", "alle"].map((z) => (
          <button
            key={z}
            onClick={() => setZeitraum(z)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              zeitraum === z ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
            data-testid={`zeitraum-${z}`}
          >
            {zeitraumLabels[z]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          {tab === "uebersicht" && <UebersichtTab stats={stats} offenePosten={offenePosten} />}
          {tab === "buchungen" && (
            <BuchungenTab buchungen={filtered} search={search} setSearch={setSearch}
              typFilter={typFilter} setTypFilter={setTypFilter}
              onDelete={handleDeleteBuchung} onUndoPayment={handleUndoPayment} onEdit={(b) => setEditBuchung(b)}
              onUploadBeleg={handleUploadBeleg} onDownloadBeleg={handleDownloadBeleg}
              onDeleteBeleg={handleDeleteBeleg} />
          )}
          {tab === "kassenbuch" && <KassenbuchTab zeitraum={zeitraum} />}
          {tab === "posten" && <OffenePostenTab posten={offenePosten} onGebucht={loadAll} />}
          {tab === "monatsabschluss" && <MonatsabschlussTab />}
          {tab === "ust" && <UstTab stats={stats} zeitraum={zeitraum} />}
          {tab === "uebung" && <UebungTab />}
        </>
      )}

      {(showNewForm || editBuchung) && (
        <BuchungFormModal buchung={editBuchung} kategorien={kategorien}
          onClose={() => { setShowNewForm(false); setEditBuchung(null); }}
          onSaved={() => { setShowNewForm(false); setEditBuchung(null); loadAll(); }} />
      )}
      {showKategorien && (
        <KategorienModal kategorien={kategorien}
          onClose={() => setShowKategorien(false)}
          onSaved={() => { setShowKategorien(false); loadAll(); }} />
      )}
      {showHilfe && <HilfeOverlay onClose={() => setShowHilfe(false)} />}
    </div>
  );
};


// ==================== HILFE OVERLAY ====================
const HilfeOverlay = ({ onClose }) => {
  const [openSection, setOpenSection] = useState(null);
  const toggle = (s) => setOpenSection(openSection === s ? null : s);

  const sections = [
    {
      key: "start", title: "Erste Schritte",
      content: `Die Buchhaltung hilft Ihnen, alle Einnahmen und Ausgaben Ihres Betriebs zu erfassen und auszuwerten.\n\n1. Klicken Sie auf "Neue Buchung" um eine Einnahme oder Ausgabe zu erfassen\n2. Wählen Sie den Typ (Einnahme/Ausgabe) und eine Kategorie\n3. Geben Sie den Betrag ein – Netto oder Brutto, das andere wird automatisch berechnet\n4. Jede Buchung erhält automatisch eine fortlaufende Belegnummer`
    },
    {
      key: "uebersicht", title: "Übersicht (Dashboard)",
      content: `Die Übersicht zeigt auf einen Blick:\n• Gesamte Einnahmen und Ausgaben (brutto)\n• Gewinn/Verlust\n• Monatliche Entwicklung als Balkendiagramm\n• Aufschlüsselung nach Kategorien\n\nMit den Zeitraum-Filtern (Monat/Quartal/Jahr/Gesamt) können Sie den Betrachtungszeitraum anpassen.`
    },
    {
      key: "buchungen", title: "Buchungen verwalten",
      content: `Alle Buchungen werden chronologisch aufgelistet.\n\n• Stift-Symbol: Buchung bearbeiten\n• Pfeil-Symbol „Rückgängig“: Buchung zurücknehmen – nach „Wirklich zurücknehmen?“ legt die Suite automatisch eine Storno-Buchung mit eigener Belegnummer an\n• Filter: Nach Einnahmen/Ausgaben filtern\n• Suche: Nach Beschreibung, Kategorie, Kunde oder Belegnummer suchen\n\nWichtig: Buchungen werden nie gelöscht. Die zurückgenommene Buchung bleibt sichtbar („storniert“), die Storno-Buchung hebt sie auf – zusammen ergeben beide null. Belegnummern werden nie doppelt vergeben.`
    },
    {
      key: "kassenbuch", title: "Kassenbuch",
      content: `Das Kassenbuch zeigt alle Buchungen chronologisch mit laufendem Saldo.\n\nSo sehen Sie zu jedem Zeitpunkt, wie hoch Ihr Kassenstand war. Der Endsaldo wird oben angezeigt.`
    },
    {
      key: "posten", title: "Offene Posten",
      content: `Hier sehen Sie alles, was noch bezahlt werden muss:\n• Anzahlungsforderungen aus Auftragsbestätigungen\n• offene Rechnungen (mit dem noch offenen Rest)\n\n"Zahlung buchen": Betrag und Datum laut Kontoauszug (N26) eingeben, Überweisung oder Bar wählen, prüfen, "Wirklich buchen?" bestätigen. Teilzahlungen sind möglich, mehr als offen wird nicht gebucht.\nBei einer Anzahlung wird danach automatisch die Anzahlungsrechnung vorbereitet.\n\nRückgängig: unter "Buchungen" mit dem Pfeil-Symbol – bei Anzahlungen nur, solange die Anzahlungsrechnung noch nicht versendet ist.`
    },
    {
      key: "monatsabschluss", title: "Monatsabschluss",
      content: `Die Monatsübersicht zeigt für jeden Monat:\n• Einnahmen und Ausgaben\n• Gewinn/Verlust\n• USt-Zahllast\n• Anzahl der Buchungen\n\nPerfekt für die monatliche Kontrolle und Vorbereitung für den Steuerberater.`
    },
    {
      key: "ust", title: "USt/MwSt-Übersicht",
      content: `Zeigt die Umsatzsteuer-Berechnung:\n\n• Umsatzsteuer: MwSt die Sie von Kunden eingenommen haben\n• Vorsteuer: MwSt die Sie bei Einkäufen bezahlt haben\n• Zahllast: Differenz – diesen Betrag schulden Sie dem Finanzamt\n  (oder bekommen zurück, wenn Vorsteuer > Umsatzsteuer)`
    },
    {
      key: "belege", title: "Belege anhängen",
      content: `Zu jeder Buchung können Sie Belege anhängen (Quittungen, Rechnungen, Fotos).\n\n• Klicken Sie auf das Büroklammer-Symbol neben einer Buchung\n• Wählen Sie eine Datei (JPG, PNG, PDF, Word, Excel, etc.)\n• Mehrere Belege pro Buchung sind möglich\n• Belege können heruntergeladen und gelöscht werden\n• Beleg-Dateinamen werden im CSV-Export mit aufgelistet\n\nSo haben Sie alles an einem Ort – perfekt für den Steuerberater.`
    },
    {
      key: "plausibilitaet", title: "Plausibilitätsprüfung",
      content: `Bei jeder neuen Buchung wird automatisch geprüft:\n\n• Ist eine Kategorie angegeben?\n• Ist der Betrag plausibel (nicht 0, nicht ungewöhnlich hoch)?\n• Liegt das Datum in der Zukunft?\n• Gibt es bereits eine ähnliche Buchung (Doppelbuchungs-Erkennung)?\n\nWarnungen werden gelb angezeigt, Fehler rot. Sie können trotzdem speichern.`
    },
    {
      key: "export", title: "CSV-Export",
      content: `Mit "CSV-Export" laden Sie alle Buchungen des gewählten Zeitraums als CSV-Datei herunter.\n\nDie Datei enthält: Belegnr., Datum, Typ, Kategorie, Beschreibung, Kunde, Netto, MwSt, Brutto, Rechnungs-Nr.\n\nDiese Datei können Sie direkt an Ihren Steuerberater weitergeben.`
    },
    {
      key: "kategorien", title: "Kategorien verwalten",
      content: `Unter "Kategorien" können Sie frei konfigurieren, welche Kategorien für Einnahmen und Ausgaben verfügbar sind.\n\nBeispiele für Ausgaben: Material, Werkzeug, Fahrzeug, Versicherung, Personal\nBeispiele für Einnahmen: Rechnung, Bareinnahme, Sonstige\n\nKategorien können jederzeit hinzugefügt, umbenannt oder gelöscht werden.`
    },
  ];

  return (
    <Modal isOpen onClose={onClose} title="Buchhaltung – Bedienungsanleitung" size="lg">
      <div className="space-y-2 max-h-[70vh] overflow-y-auto" data-testid="hilfe-overlay">
        {sections.map((s) => (
          <div key={s.key} className="border rounded-sm overflow-hidden">
            <button
              onClick={() => toggle(s.key)}
              className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
              data-testid={`hilfe-${s.key}`}
            >
              <span className="text-sm font-semibold">{s.title}</span>
              {openSection === s.key ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>
            {openSection === s.key && (
              <div className="px-4 pb-3 text-sm text-muted-foreground whitespace-pre-line animate-in fade-in slide-in-from-top-1 duration-200">
                {s.content}
              </div>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
};


// ==================== ÜBERSICHT TAB ====================
const UebersichtTab = ({ stats, offenePosten }) => {
  const offenTotal = (offenePosten || []).reduce((sum, p) => sum + (p.betrag || 0), 0);
  if (!stats) return null;
  const maxMonat = Math.max(...(stats.monatlich || []).map(m => Math.max(m.einnahmen, m.ausgaben)), 1);

  return (
    <div className="space-y-6" data-testid="tab-content-uebersicht">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Einnahmen (brutto)</p>
              <p className="text-2xl font-bold text-green-600" data-testid="kpi-einnahmen">
                {stats.einnahmen_brutto.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </p>
            </div>
            <ArrowUpCircle className="w-8 h-8 text-green-500/30" />
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Ausgaben (brutto)</p>
              <p className="text-2xl font-bold text-red-600" data-testid="kpi-ausgaben">
                {stats.ausgaben_brutto.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </p>
            </div>
            <ArrowDownCircle className="w-8 h-8 text-red-500/30" />
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Gewinn (brutto)</p>
              <p className={`text-2xl font-bold ${stats.gewinn_brutto >= 0 ? "text-green-600" : "text-red-600"}`} data-testid="kpi-gewinn">
                {stats.gewinn_brutto.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </p>
            </div>
            {stats.gewinn_brutto >= 0 ? <TrendingUp className="w-8 h-8 text-green-500/30" /> : <TrendingDown className="w-8 h-8 text-red-500/30" />}
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Offene Forderungen</p>
              <p className="text-2xl font-bold text-amber-600" data-testid="kpi-offene-forderungen">
                {offenTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </p>
              <p className="text-xs text-muted-foreground">{(offenePosten || []).length} offene Rechnung(en)</p>
            </div>
            <CreditCard className="w-8 h-8 text-amber-500/30" />
          </div>
        </Card>
      </div>

      {stats.monatlich?.length > 0 && (
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-4">Monatliche Übersicht</h3>
          <div className="space-y-3">
            {stats.monatlich.map((m) => {
              const monatLabel = (() => { try { const [y, mo] = m.monat.split("-"); return new Date(y, parseInt(mo) - 1).toLocaleDateString("de-DE", { month: "short", year: "2-digit" }); } catch { return m.monat; } })();
              return (
                <div key={m.monat} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-muted-foreground w-16 shrink-0">{monatLabel}</span>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="h-3 rounded-full bg-green-500 transition-all" style={{ width: `${(m.einnahmen / maxMonat) * 100}%`, minWidth: m.einnahmen > 0 ? "4px" : "0" }} />
                      <span className="text-xs text-green-700">{m.einnahmen.toLocaleString("de-DE")} EUR</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 rounded-full bg-red-400 transition-all" style={{ width: `${(m.ausgaben / maxMonat) * 100}%`, minWidth: m.ausgaben > 0 ? "4px" : "0" }} />
                      <span className="text-xs text-red-700">{m.ausgaben.toLocaleString("de-DE")} EUR</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500" /> Einnahmen</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-400" /> Ausgaben</span>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.keys(stats.kategorien_einnahmen || {}).length > 0 && (
          <Card className="p-5">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><ArrowUpCircle className="w-4 h-4 text-green-600" /> Einnahmen nach Kategorie</h3>
            <div className="space-y-2">{Object.entries(stats.kategorien_einnahmen).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm"><span>{k}</span><span className="font-medium text-green-700">{v.toLocaleString("de-DE")} EUR</span></div>
            ))}</div>
          </Card>
        )}
        {Object.keys(stats.kategorien_ausgaben || {}).length > 0 && (
          <Card className="p-5">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><ArrowDownCircle className="w-4 h-4 text-red-600" /> Ausgaben nach Kategorie</h3>
            <div className="space-y-2">{Object.entries(stats.kategorien_ausgaben).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm"><span>{k}</span><span className="font-medium text-red-700">{v.toLocaleString("de-DE")} EUR</span></div>
            ))}</div>
          </Card>
        )}
      </div>
    </div>
  );
};


// ==================== BUCHUNGEN TAB ====================
const BuchungenTab = ({ buchungen, search, setSearch, typFilter, setTypFilter, onDelete, onUndoPayment, onEdit, onUploadBeleg, onDownloadBeleg, onDeleteBeleg }) => {
  // Belege loeschen nur fuer Admin (07.10.2026)
  const istAdmin = (() => { try { return JSON.parse(localStorage.getItem("user") || "null")?.role === "admin"; } catch { return false; } })();
  // 07.10.2026: ein Knopf "Rückgängig" -> "Wirklich zurücknehmen?" -> Gegenbuchung (automatisch)
  const rueckgaengig = (b) => {
    if (!window.confirm(`Buchung ${b.belegnummer || ""} über ${Math.abs(Number(b.betrag_brutto || 0)).toFixed(2)} € wirklich zurücknehmen?`)) return;
    if (b.rechnung_id) onUndoPayment(b); else onDelete(b.id);
  };
  const betragText = (b) => {
    const v = Number(b.betrag_brutto || 0);
    const plus = (b.typ === "einnahme") === (v >= 0);
    return `${plus ? "+" : "−"}${Math.abs(v).toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR`;
  };

  const handleFileSelect = (buchungId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt";
    input.onchange = (e) => {
      if (e.target.files[0]) onUploadBeleg(buchungId, e.target.files[0]);
    };
    input.click();
  };

  const belegIcon = (filename) => {
    const ext = (filename || "").split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return <ImageIcon className="w-3 h-3" />;
    if (ext === "pdf") return <FileText className="w-3 h-3" />;
    return <FileIcon className="w-3 h-3" />;
  };

  return (
    <div data-testid="tab-content-buchungen">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["alle", "einnahme", "ausgabe"].map((t) => (
          <button key={t} onClick={() => setTypFilter(t)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${typFilter === t ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
            data-testid={`typ-filter-${t}`}
          >{t === "alle" ? "Alle" : t === "einnahme" ? "Einnahmen" : "Ausgaben"}</button>
        ))}
      </div>
      <Card className="p-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input className="pl-10" placeholder="Buchungen durchsuchen (Beschreibung, Kategorie, Belegnr.)..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="input-search-buchungen" />
        </div>
      </Card>

      {buchungen.length === 0 ? (
        <Card className="p-12 text-center"><Receipt className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground">Keine Buchungen vorhanden</p></Card>
      ) : (
        <div className="space-y-2">
          {buchungen.map((b) => (
            <Card key={b.id} className={`p-4 ${b.storniert || b.storno_zu ? "bg-muted/40" : ""}`} data-testid={`buchung-${b.id}`}>
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-sm shrink-0 ${b.typ === "einnahme" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
                  {b.typ === "einnahme" ? <ArrowUpCircle className="w-4 h-4" /> : <ArrowDownCircle className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {b.belegnummer && <span className="text-xs font-mono text-primary font-semibold">{b.belegnummer}</span>}
                    <span className={`font-medium text-sm ${b.storniert ? "line-through text-muted-foreground" : ""}`}>{b.beschreibung || "Keine Beschreibung"}</span>
                    {b.storniert && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700" data-testid={`storniert-${b.id}`}>storniert durch {b.storno_beleg}</span>}
                    {b.storno_zu && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-700" data-testid={`storno-zu-${b.id}`}>Storno zu {b.storno_zu_beleg}</span>}
                    {b.kategorie && <Badge variant="default" className="text-xs">{b.kategorie}</Badge>}
                  </div>
                  <div className="flex gap-x-4 gap-y-1 flex-wrap mt-1 text-xs text-muted-foreground">
                    {b.kunde && <span>Kunde: {b.kunde}</span>}
                    {b.rechnung_nr && <span>Re: {b.rechnung_nr}</span>}
                    <span>{new Date(b.datum).toLocaleDateString("de-DE")}</span>
                  </div>
                  {b.warnungen?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {b.warnungen.map((w, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-sm flex items-center gap-0.5">
                          <AlertTriangle className="w-3 h-3" /> {typeof w === "string" ? w : w.text}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Belege */}
                  {b.belege?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {b.belege.map((bl) => (
                        <span key={bl.id} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-sm group" data-testid={`beleg-${bl.id}`}>
                          {belegIcon(bl.original_filename)}
                          <button onClick={() => onDownloadBeleg(bl)} className="hover:underline truncate max-w-[120px]" title={bl.original_filename}>
                            {bl.original_filename}
                          </button>
                          {istAdmin && (
                            <button onClick={() => onDeleteBeleg(b.id, bl.id, bl.original_filename)} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity" title="Beleg löschen (nur Admin)" data-testid={`btn-beleg-loeschen-${bl.id}`}>
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`font-bold text-sm ${b.storniert || b.storno_zu ? "text-muted-foreground" : b.typ === "einnahme" ? "text-green-600" : "text-red-600"}`}>
                    {betragText(b)}
                  </span>
                  <button onClick={() => handleFileSelect(b.id)} className="p-2 rounded-sm text-muted-foreground hover:bg-blue-50 hover:text-blue-600 transition-colors" title="Beleg anhängen" data-testid={`btn-beleg-${b.id}`}>
                    <Paperclip className="w-4 h-4" />
                  </button>
                  {!(b.storniert || b.storno_zu) && (<>
                    <button onClick={() => onEdit(b)} className="p-2 rounded-sm text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors" title="Bearbeiten" data-testid={`btn-edit-buchung-${b.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => rueckgaengig(b)}
                      className="p-2 rounded-sm text-muted-foreground/60 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                      title="Rückgängig (die Suite legt automatisch eine Storno-Buchung an)" data-testid={b.rechnung_id ? `btn-undo-payment-${b.id}` : `btn-delete-buchung-${b.id}`}>
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  </>)}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


// ==================== KASSENBUCH TAB ====================
const KassenbuchTab = ({ zeitraum }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/buchhaltung/kassenbuch", { params: { zeitraum } })
      .then(res => setData(res.data))
      .catch(() => toast.error("Fehler beim Laden des Kassenbuchs"))
      .finally(() => setLoading(false));
  }, [zeitraum]);

  if (loading) return <div className="text-center py-12 text-muted-foreground">Lade Kassenbuch...</div>;
  if (!data) return null;

  return (
    <div data-testid="tab-content-kassenbuch">
      <Card className="p-4 mb-4 bg-blue-50 border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-blue-800">Kassenbuch – Laufender Saldo</p>
            <p className="text-xs text-blue-700">{data.eintraege.length} Buchungen im Zeitraum</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-blue-700">Endsaldo</p>
            <p className={`text-lg font-bold ${data.endsaldo >= 0 ? "text-green-700" : "text-red-700"}`} data-testid="kassenbuch-endsaldo">
              {data.endsaldo.toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR
            </p>
          </div>
        </div>
      </Card>

      {data.eintraege.length === 0 ? (
        <Card className="p-12 text-center"><BookOpen className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground">Keine Einträge im Kassenbuch</p></Card>
      ) : (
        <div className="border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-semibold text-xs">Beleg-Nr.</th>
                <th className="text-left p-3 font-semibold text-xs">Datum</th>
                <th className="text-left p-3 font-semibold text-xs">Beschreibung</th>
                <th className="text-left p-3 font-semibold text-xs">Kategorie</th>
                <th className="text-right p-3 font-semibold text-xs">Einnahme</th>
                <th className="text-right p-3 font-semibold text-xs">Ausgabe</th>
                <th className="text-right p-3 font-semibold text-xs">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {data.eintraege.map((e, i) => (
                <tr key={e.id || i} className="border-t hover:bg-muted/20 transition-colors" data-testid={`kassenbuch-row-${i}`}>
                  <td className="p-3 font-mono text-xs text-primary">{e.belegnummer || "-"}</td>
                  <td className="p-3 text-xs">{new Date(e.datum).toLocaleDateString("de-DE")}</td>
                  <td className="p-3 text-xs">{e.beschreibung || "-"}</td>
                  <td className="p-3 text-xs">{e.kategorie || "-"}</td>
                  <td className="p-3 text-xs text-right text-green-700 font-medium">
                    {e.typ === "einnahme" ? `${(e.betrag_brutto || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}` : ""}
                  </td>
                  <td className="p-3 text-xs text-right text-red-700 font-medium">
                    {e.typ === "ausgabe" ? `${(e.betrag_brutto || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}` : ""}
                  </td>
                  <td className={`p-3 text-xs text-right font-bold ${e.saldo >= 0 ? "text-green-700" : "text-red-700"}`}>
                    {e.saldo.toLocaleString("de-DE", { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


// ==================== MONATSABSCHLUSS TAB ====================
const MonatsabschlussTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jahr, setJahr] = useState(new Date().getFullYear());

  useEffect(() => {
    setLoading(true);
    api.get("/buchhaltung/monatsabschluss", { params: { jahr } })
      .then(res => setData(res.data))
      .catch(() => toast.error("Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, [jahr]);

  const monatName = (m) => {
    try { const [y, mo] = m.split("-"); return new Date(y, parseInt(mo) - 1).toLocaleDateString("de-DE", { month: "long" }); }
    catch { return m; }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Lade Monatsabschluss...</div>;

  return (
    <div data-testid="tab-content-monatsabschluss">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => setJahr(jahr - 1)} className="px-3 py-1.5 bg-muted rounded-sm text-sm font-medium hover:bg-muted/80">&larr; {jahr - 1}</button>
        <span className="text-lg font-bold" data-testid="monatsabschluss-jahr">{jahr}</span>
        <button onClick={() => setJahr(jahr + 1)} className="px-3 py-1.5 bg-muted rounded-sm text-sm font-medium hover:bg-muted/80">{jahr + 1} &rarr;</button>
      </div>

      {!data?.monate?.length ? (
        <Card className="p-12 text-center"><CalendarDays className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground">Keine Buchungen in {jahr}</p></Card>
      ) : (
        <div className="border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-semibold text-xs">Monat</th>
                <th className="text-right p-3 font-semibold text-xs">Einnahmen</th>
                <th className="text-right p-3 font-semibold text-xs">Ausgaben</th>
                <th className="text-right p-3 font-semibold text-xs">Gewinn</th>
                <th className="text-right p-3 font-semibold text-xs">USt-Zahllast</th>
                <th className="text-right p-3 font-semibold text-xs">Buchungen</th>
              </tr>
            </thead>
            <tbody>
              {data.monate.map((m) => (
                <tr key={m.monat} className="border-t hover:bg-muted/20 transition-colors" data-testid={`monat-${m.monat}`}>
                  <td className="p-3 font-medium">{monatName(m.monat)}</td>
                  <td className="p-3 text-right text-green-700 font-medium">{m.einnahmen.toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right text-red-700 font-medium">{m.ausgaben.toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                  <td className={`p-3 text-right font-bold ${m.gewinn >= 0 ? "text-green-700" : "text-red-700"}`}>{m.gewinn.toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                  <td className={`p-3 text-right ${m.zahllast >= 0 ? "text-amber-700" : "text-green-700"}`}>{m.zahllast.toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right text-muted-foreground">{m.anzahl}</td>
                </tr>
              ))}
              {/* Summenzeile */}
              <tr className="border-t-2 bg-muted/30 font-bold">
                <td className="p-3">Gesamt {jahr}</td>
                <td className="p-3 text-right text-green-700">{data.monate.reduce((s, m) => s + m.einnahmen, 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                <td className="p-3 text-right text-red-700">{data.monate.reduce((s, m) => s + m.ausgaben, 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                <td className="p-3 text-right">{data.monate.reduce((s, m) => s + m.gewinn, 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                <td className="p-3 text-right">{data.monate.reduce((s, m) => s + m.zahllast, 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                <td className="p-3 text-right">{data.monate.reduce((s, m) => s + m.anzahl, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


// ==================== OFFENE POSTEN TAB ====================
const eur = (v) => Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const OffenePostenTab = ({ posten, onGebucht }) => {
  const navigate = useNavigate();
  const total = posten.reduce((sum, p) => sum + (p.betrag || 0), 0);
  const [auswahl, setAuswahl] = useState(null);
  const [unversendet, setUnversendet] = useState([]);
  const ladeUnversendet = () => api.get("/buchhaltung/anzahlungsrechnungen-unversendet").then(r => setUnversendet(r.data || [])).catch(() => {});
  useEffect(() => { ladeUnversendet(); }, [posten]);

  return (
    <div data-testid="tab-content-posten">
      <Card className="p-4 mb-4 bg-amber-50 border-amber-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-amber-800">{posten.length} offene Posten</p>
            <p className="text-xs text-amber-700">Gesamtbetrag offen: {total.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</p>
          </div>
          <CreditCard className="w-6 h-6 text-amber-400" />
        </div>
      </Card>
      {unversendet.length > 0 && (
        <Card className="p-3 mb-4 border-blue-200 bg-blue-50/50" data-testid="anzahlungsrechnungen-unversendet">
          <p className="text-sm font-medium text-blue-900 mb-1">Vorbereitete Anzahlungsrechnungen – noch nicht versendet</p>
          {unversendet.map(r => (
            <div key={r.id} className="flex items-center justify-between text-xs py-0.5">
              <span>{r.invoice_number} · {r.customer_name} · {eur(r.total_gross)}</span>
              <button className="text-primary hover:underline" onClick={() => navigate(`/invoices/edit/${r.id}`)}>prüfen und versenden</button>
            </div>
          ))}
        </Card>
      )}
      {posten.length === 0 ? (
        <Card className="p-12 text-center"><Check className="w-12 h-12 text-green-500/30 mx-auto mb-3" /><p className="text-muted-foreground">Keine offenen Posten – alles bezahlt!</p></Card>
      ) : (
        <div className="space-y-2">
          {posten.map((p) => {
            const anz = p.posten_art === "anzahlungsforderung";
            return (
              <Card key={`${p.posten_art}-${p.id}`} className={`p-4 ${p.status === "Überfällig" ? "border-red-300 bg-red-50/30" : anz ? "border-purple-200" : ""}`} data-testid={`posten-${p.id}`}>
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-sm shrink-0 ${p.status === "Überfällig" ? "bg-red-100 text-red-600" : anz ? "bg-purple-50 text-purple-600" : "bg-amber-50 text-amber-600"}`}><Receipt className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{anz ? `Anzahlungsforderung ${p.nummer}` : p.nummer}</span>
                      <Badge variant={p.status === "Überfällig" ? "destructive" : "default"} className="text-xs">{p.status}</Badge>
                    </div>
                    <div className="flex gap-x-4 gap-y-1 flex-wrap mt-1 text-xs text-muted-foreground">
                      <span>Kunde: {p.kunde}</span>
                      <span>{anz ? "vereinbart" : "Betrag"}: {eur(p.gesamt)}</span>
                      {p.gebucht > 0 && <span>gebucht: {eur(p.gebucht)}</span>}
                      {p.faellig_am && <span>Fällig: {new Date(p.faellig_am).toLocaleDateString("de-DE")}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-bold text-sm">offen {eur(p.offen)}</span>
                    <Button size="sm" variant="outline" onClick={() => setAuswahl(p)} data-testid={`btn-zahlung-buchen-${p.id}`} title="Zahlungseingang laut Kontoauszug buchen">
                      <Check className="w-3.5 h-3.5 mr-1" /> Zahlung buchen
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      {auswahl && <ZahlungBuchenDialog posten={auswahl} onClose={() => setAuswahl(null)}
        onGebucht={() => { onGebucht(); ladeUnversendet(); }} navigate={navigate} />}
    </div>
  );
};

// A-024: Übung (Testkunden/Übungsbelege). Nichts davon zählt in Übersicht, Kassenbuch, Monatsabschluss, USt, CSV oder Dashboard.
const UebungTab = () => {
  const navigate = useNavigate();
  const [posten, setPosten] = useState([]);
  const [buchungen, setBuchungen] = useState([]);
  const [auswahl, setAuswahl] = useState(null);
  const laden = async () => {
    try {
      const [p, b] = await Promise.all([
        api.get("/buchhaltung/offene-posten", { params: { uebung: true } }),
        api.get("/buchhaltung/buchungen", { params: { zeitraum: "alle", uebung: "nur" } }),
      ]);
      setPosten(p.data); setBuchungen(b.data);
    } catch { toast.error("Übungsdaten konnten nicht geladen werden"); }
  };
  useEffect(() => { laden(); }, []);
  const loeschen = async (b) => {
    if (!window.confirm(`Übungsbuchung ${b.belegnummer} löschen? Sie zählt nirgends mit.`)) return;
    try {
      if (b.zahlung_id) await api.post(`/buchhaltung/zahlung-rueckgaengig-buchung/${b.id}`);
      else await api.delete(`/buchhaltung/buchungen/${b.id}`);
      toast.success("Übungsbuchung gelöscht"); laden();
    } catch (err) { toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };
  return (
    <div data-testid="uebung-tab" className="space-y-4">
      <Card className="p-3 border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="uebung-hinweis">
        <b>Übung – zählt nicht echt.</b> Hier stehen die Posten und Buchungen von Testkunden und Übungsbelegen. Sie haben eigene Nummern
        (TEST-B-…), tauchen in keiner Auswertung auf und lassen sich jederzeit löschen.
      </Card>
      <h3 className="font-semibold text-sm">Offene Übungsposten</h3>
      {posten.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="uebung-posten-leer">Keine offenen Übungsposten.</p> : (
        <div className="space-y-2">
          {posten.map((p) => (
            <Card key={`${p.posten_art}-${p.id}`} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`uebung-posten-${p.id}`}>
              <TestMarke />
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-medium">{p.posten_art === "anzahlungsforderung" ? `Anzahlungsforderung ${p.nummer}` : p.nummer}</div>
                <div className="text-xs text-muted-foreground">Kunde: {p.kunde} · offen {eur(p.offen)}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setAuswahl(p)} data-testid={`btn-uebung-zahlung-buchen-${p.id}`}>
                <Check className="w-3.5 h-3.5 mr-1" /> Zahlung buchen (Übung)
              </Button>
            </Card>
          ))}
        </div>
      )}
      <h3 className="font-semibold text-sm">Übungsbuchungen</h3>
      {buchungen.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="uebung-buchungen-leer">Keine Übungsbuchungen.</p> : (
        <div className="space-y-2">
          {buchungen.map((b) => (
            <Card key={b.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`uebung-buchung-${b.id}`}>
              <TestMarke />
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-medium">{b.belegnummer} · {eur(b.betrag_brutto)}</div>
                <div className="text-xs text-muted-foreground truncate">{b.beschreibung}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => loeschen(b)} data-testid={`btn-uebung-loeschen-${b.id}`}>
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen
              </Button>
            </Card>
          ))}
        </div>
      )}
      {auswahl && <ZahlungBuchenDialog posten={auswahl} onClose={() => setAuswahl(null)} onGebucht={laden} navigate={navigate} />}
    </div>
  );
};

const N26_URL = "https://app.n26.com/";

const inZwischenablage = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      const ok = document.execCommand("copy"); ta.remove(); return ok;
    } catch { return false; }
  }
};

// Zahlung buchen (06.10.2026): Betrag laut N26 -> Vergleich -> "Wirklich buchen?" -> Buchung
// -> bei Anzahlung: "Rechnung jetzt prüfen und versenden?"
const ZahlungBuchenDialog = ({ posten, onClose, onGebucht, navigate }) => {
  const heute = new Date().toISOString().slice(0, 10);
  const [schritt, setSchritt] = useState("eingabe"); // eingabe | bestaetigen | fertig
  const [betrag, setBetrag] = useState("");
  const [datum, setDatum] = useState(heute);
  const [zahlungsart, setZahlungsart] = useState("ueberweisung");
  const [notiz, setNotiz] = useState("");
  const [pruefung, setPruefung] = useState(null);
  const [ergebnis, setErgebnis] = useState(null);
  const [busy, setBusy] = useState(false);
  const anz = posten.posten_art === "anzahlungsforderung";
  const b = parseFloat(String(betrag).replace(",", "."));
  const vergleich = !(b > 0) ? null
    : b > posten.offen + 0.005 ? { typ: "mehr", text: `Mehr als offen (${eur(posten.offen)}) – wird nicht gebucht. Bitte von Hand klären.`, cls: "bg-red-50 border-red-300 text-red-800" }
    : Math.abs(b - posten.offen) < 0.005 ? { typ: "gleich", text: "Betrag stimmt mit dem offenen Betrag überein – vollständig bezahlt.", cls: "bg-green-50 border-green-300 text-green-800" }
    : { typ: "teil", text: `Teilzahlung – danach bleiben ${eur(posten.offen - b)} offen.`, cls: "bg-amber-50 border-amber-300 text-amber-900" };
  const daten = { posten_art: posten.posten_art, ref_id: posten.ref_id, betrag: b, datum, zahlungsart, notiz, quelle: "manuell" };

  // "In N26 nachsehen" (06.10.2026): ein Klick = Nummer + Name kopieren UND N26 in neuem Fenster.
  // Erst kopieren (solange diese Seite den Fokus hat), dann das Fenster oeffnen.
  const inN26Nachsehen = () => {
    const t = `${posten.nummer} ${posten.kunde || ""}`.trim();
    let sofort = false;
    try {
      const ta = document.createElement("textarea");
      ta.value = t; document.body.appendChild(ta); ta.select();
      sofort = document.execCommand("copy"); ta.remove();
    } catch { sofort = false; }
    const kopiert = sofort ? Promise.resolve(true) : inZwischenablage(t);
    window.open(N26_URL, "_blank", "noopener");
    kopiert.then((ok) => toast[ok ? "success" : "error"](ok ? `„${t}" kopiert – in N26 ins Suchfeld einfügen` : "Kopieren nicht möglich – N26 ist trotzdem geöffnet"));
  };
  const weiter = async () => {
    setBusy(true);
    try { const r = await api.post("/buchhaltung/zahlung-pruefen", daten); setPruefung(r.data); setSchritt("bestaetigen"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Prüfung fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const buchen = async () => {
    setBusy(true);
    try {
      const r = await api.post("/buchhaltung/zahlung-buchen", daten);
      setErgebnis(r.data); setSchritt("fertig"); onGebucht();
      toast.success(`Gebucht: ${eur(b)} (Beleg ${r.data.buchung.belegnummer})`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Buchung fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="zahlung-buchen-dialog">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-md">
        <div className="p-5 border-b">
          <h3 className="text-lg font-semibold">{schritt === "bestaetigen" ? "Wirklich buchen?" : schritt === "fertig" ? "Gebucht" : "Zahlung buchen"}</h3>
          <p className="text-sm text-muted-foreground mt-1">{anz ? "Anzahlungsforderung" : "Rechnung"} {posten.nummer} · {posten.kunde} · offen {eur(posten.offen)}</p>
        </div>

        {schritt === "eingabe" && (
          <div className="p-5 space-y-3">
            <button type="button" onClick={inN26Nachsehen} className="px-3 py-1.5 text-sm border rounded-sm hover:bg-muted" data-testid="btn-n26-nachsehen"
              title={`Kopiert „${posten.nummer} ${posten.kunde || ""}“ und öffnet N26`}>In N26 nachsehen</button>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">Betrag laut Kontoauszug (brutto)</label>
              <input type="number" step="0.01" min="0" value={betrag} onChange={(e) => setBetrag(e.target.value)} autoFocus placeholder={posten.offen.toFixed(2)}
                className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm font-mono" data-testid="input-zahlung-betrag" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">Datum des Eingangs</label>
                <input type="date" value={datum} max={heute} onChange={(e) => setDatum(e.target.value)}
                  className="w-full h-9 rounded-sm border border-input bg-background px-2 text-sm" data-testid="input-zahlung-datum" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">Zahlungsart</label>
                <div className="flex gap-3 h-9 items-center text-sm">
                  <label className="flex items-center gap-1"><input type="radio" checked={zahlungsart === "ueberweisung"} onChange={() => setZahlungsart("ueberweisung")} data-testid="radio-ueberweisung" /> Überweisung</label>
                  <label className="flex items-center gap-1"><input type="radio" checked={zahlungsart === "bar"} onChange={() => setZahlungsart("bar")} data-testid="radio-bar" /> Bar</label>
                </div>
              </div>
            </div>
            <input value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Notiz (optional, z. B. Verwendungszweck)"
              className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm" data-testid="input-zahlung-notiz" />
            {vergleich && <div className={`p-2 rounded-sm border text-sm ${vergleich.cls}`} data-testid="zahlung-vergleich">{vergleich.text}</div>}
          </div>
        )}

        {schritt === "bestaetigen" && pruefung && (
          <div className="p-5 space-y-1.5 text-sm" data-testid="zahlung-bestaetigen">
            <div className="flex justify-between"><span className="text-muted-foreground">Betrag</span><span className="font-semibold">{eur(pruefung.betrag)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">davon Netto / MwSt ({Number(pruefung.mwst_satz).toFixed(0)} %)</span><span>{eur(pruefung.netto)} / {eur(pruefung.mwst)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Datum / Art</span><span>{new Date(pruefung.datum).toLocaleDateString("de-DE")} · {pruefung.zahlungsart === "bar" ? "Bar" : "Überweisung"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Belegnummer</span><span className="font-mono">{pruefung.belegnummer_vorschau}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">danach offen</span><span>{eur(pruefung.rest)}</span></div>
            {anz && <p className="text-xs text-muted-foreground pt-2">Nach dem Buchen wird automatisch eine Anzahlungsrechnung über {eur(pruefung.betrag)} vorbereitet.</p>}
            {pruefung.warnungen?.map((w, i) => <p key={i} className="text-xs text-amber-800 bg-amber-50 p-2 rounded-sm">{w}</p>)}
          </div>
        )}

        {schritt === "fertig" && ergebnis && (
          <div className="p-5 text-sm space-y-2" data-testid="zahlung-fertig">
            <p>Beleg <span className="font-mono">{ergebnis.buchung.belegnummer}</span> über {eur(ergebnis.buchung.betrag_brutto)} ist gebucht{ergebnis.rest > 0 ? `, offen bleiben ${eur(ergebnis.rest)}` : ""}.</p>
            {ergebnis.anzahlungsrechnung_vorbereitet && <p className="font-medium">Anzahlungsrechnung {ergebnis.rechnung_nr} ist vorbereitet. Rechnung jetzt prüfen und versenden?</p>}
          </div>
        )}

        <div className="p-4 border-t flex justify-end gap-2">
          {schritt === "eingabe" && (<>
            <button onClick={onClose} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted">Abbrechen</button>
            <button onClick={weiter} disabled={busy || !vergleich || vergleich.typ === "mehr" || !datum}
              className="px-5 py-2 text-sm font-semibold rounded-sm bg-primary text-primary-foreground disabled:opacity-50" data-testid="btn-zahlung-weiter">Weiter</button>
          </>)}
          {schritt === "bestaetigen" && (<>
            <button onClick={() => setSchritt("eingabe")} disabled={busy} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted">Zurück</button>
            <button onClick={buchen} disabled={busy} className="px-5 py-2 text-sm font-semibold rounded-sm bg-green-700 text-white hover:bg-green-800 disabled:opacity-50" data-testid="btn-zahlung-ja">
              {busy ? "Buche..." : "Ja, jetzt buchen"}
            </button>
          </>)}
          {schritt === "fertig" && (ergebnis?.anzahlungsrechnung_vorbereitet ? (<>
            <button onClick={onClose} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted" data-testid="btn-rechnung-spaeter">Später</button>
            <button onClick={() => navigate(`/invoices/edit/${ergebnis.rechnung_id}`)} className="px-5 py-2 text-sm font-semibold rounded-sm bg-green-700 text-white hover:bg-green-800" data-testid="btn-rechnung-pruefen">
              Ja, Rechnung öffnen
            </button>
          </>) : (
            <button onClick={onClose} className="px-5 py-2 text-sm font-semibold rounded-sm bg-primary text-primary-foreground" data-testid="btn-zahlung-schliessen">Schließen</button>
          ))}
        </div>
      </div>
    </div>
  );
};


// ==================== UST TAB ====================
const UstTab = ({ stats, zeitraum }) => {
  if (!stats) return null;
  const zeitraumLabels = { monat: "diesen Monat", quartal: "dieses Quartal", jahr: "dieses Jahr", alle: "gesamt" };

  return (
    <div className="space-y-4" data-testid="tab-content-ust">
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-4">USt/MwSt-Übersicht ({zeitraumLabels[zeitraum]})</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center py-2 border-b"><span className="text-sm">Umsatzsteuer (eingenommen)</span><span className="font-bold text-sm">{stats.ust_einnahmen.toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR</span></div>
          <div className="flex justify-between items-center py-2 border-b"><span className="text-sm">Vorsteuer (gezahlt)</span><span className="font-bold text-sm">-{stats.vst_ausgaben.toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR</span></div>
          <div className={`flex justify-between items-center py-3 rounded-sm px-3 ${stats.ust_zahllast >= 0 ? "bg-amber-50" : "bg-green-50"}`}>
            <span className="text-sm font-semibold">{stats.ust_zahllast >= 0 ? "USt-Zahllast (an Finanzamt)" : "Vorsteuerüberhang (Erstattung)"}</span>
            <span className={`font-bold text-lg ${stats.ust_zahllast >= 0 ? "text-amber-700" : "text-green-700"}`}>{Math.abs(stats.ust_zahllast).toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR</span>
          </div>
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">Einnahmen</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm"><span>Netto</span><span>{stats.einnahmen_netto.toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR</span></div>
            <div className="flex justify-between text-sm"><span>USt</span><span>{stats.ust_einnahmen.toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR</span></div>
            <div className="flex justify-between text-sm font-semibold border-t pt-2"><span>Brutto</span><span>{stats.einnahmen_brutto.toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR</span></div>
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">Ausgaben</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm"><span>Netto</span><span>{stats.ausgaben_netto.toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR</span></div>
            <div className="flex justify-between text-sm"><span>VSt</span><span>{stats.vst_ausgaben.toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR</span></div>
            <div className="flex justify-between text-sm font-semibold border-t pt-2"><span>Brutto</span><span>{stats.ausgaben_brutto.toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR</span></div>
          </div>
        </Card>
      </div>
    </div>
  );
};


// ==================== BUCHUNG FORM MODAL mit Plausibilitätsprüfung ====================
const BuchungFormModal = ({ buchung, kategorien, onClose, onSaved }) => {
  const isEdit = !!buchung;
  const [form, setForm] = useState({
    typ: buchung?.typ || "ausgabe",
    kategorie: buchung?.kategorie || "",
    beschreibung: buchung?.beschreibung || "",
    betrag_netto: buchung?.betrag_netto || "",
    mwst_satz: buchung?.mwst_satz ?? 19,
    betrag_brutto: buchung?.betrag_brutto || "",
    datum: buchung?.datum ? buchung.datum.substring(0, 10) : new Date().toISOString().substring(0, 10),
    notizen: buchung?.notizen || "",
    kunde: buchung?.kunde || "",
  });
  const [saving, setSaving] = useState(false);
  const [warnungen, setWarnungen] = useState([]);
  const [checking, setChecking] = useState(false);

  const catOptions = form.typ === "einnahme" ? (kategorien.einnahme || []) : (kategorien.ausgabe || []);

  const handleNettoChange = (val) => {
    const netto = parseFloat(val) || 0;
    const brutto = Math.round(netto * (1 + (parseFloat(form.mwst_satz) || 0) / 100) * 100) / 100;
    setForm({ ...form, betrag_netto: val, betrag_brutto: brutto || "" });
  };

  const handleBruttoChange = (val) => {
    const brutto = parseFloat(val) || 0;
    const netto = Math.round((brutto / (1 + (parseFloat(form.mwst_satz) || 0) / 100)) * 100) / 100;
    setForm({ ...form, betrag_brutto: val, betrag_netto: netto || "" });
  };

  const handleMwstChange = (val) => {
    const mwst = parseFloat(val) || 0;
    const netto = parseFloat(form.betrag_netto) || 0;
    const brutto = Math.round(netto * (1 + mwst / 100) * 100) / 100;
    setForm({ ...form, mwst_satz: val, betrag_brutto: brutto || form.betrag_brutto });
  };

  const runPlausibility = async () => {
    setChecking(true);
    try {
      const res = await api.post("/buchhaltung/plausibilitaet", {
        ...form,
        betrag_netto: parseFloat(form.betrag_netto) || 0,
        betrag_brutto: parseFloat(form.betrag_brutto) || 0,
      });
      setWarnungen(res.data.warnungen || []);
    } catch { /* ignore */ }
    finally { setChecking(false); }
  };

  const handleSave = async () => {
    // Run plausibility check first
    setChecking(true);
    try {
      const checkRes = await api.post("/buchhaltung/plausibilitaet", {
        ...form,
        betrag_netto: parseFloat(form.betrag_netto) || 0,
        betrag_brutto: parseFloat(form.betrag_brutto) || 0,
      });
      const warns = checkRes.data.warnungen || [];
      setWarnungen(warns);

      // Block on errors
      if (warns.some(w => w.typ === "fehler")) {
        toast.error("Bitte Fehler beheben vor dem Speichern");
        setChecking(false);
        return;
      }
    } catch { /* proceed */ }
    setChecking(false);

    setSaving(true);
    try {
      const payload = {
        ...form,
        betrag_netto: parseFloat(form.betrag_netto) || 0,
        betrag_brutto: parseFloat(form.betrag_brutto) || 0,
        mwst_satz: parseFloat(form.mwst_satz) || 0,
      };
      if (isEdit) {
        await api.put(`/buchhaltung/buchungen/${buchung.id}`, payload);
        toast.success("Buchung aktualisiert");
      } else {
        await api.post("/buchhaltung/buchungen", payload);
        toast.success("Buchung erstellt");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title={isEdit ? "Buchung bearbeiten" : "Neue Buchung"} size="lg">
      <div className="space-y-4" data-testid="buchung-form-modal">
        {/* Typ */}
        <div className="flex gap-2" data-testid="buchung-typ-toggle">
          <button type="button" onClick={() => setForm({ ...form, typ: "einnahme", kategorie: "" })}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-sm font-medium transition-all ${form.typ === "einnahme" ? "bg-green-600 text-white shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
            data-testid="typ-einnahme"><ArrowUpCircle className="w-4 h-4" /> Einnahme</button>
          <button type="button" onClick={() => setForm({ ...form, typ: "ausgabe", kategorie: "" })}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-sm font-medium transition-all ${form.typ === "ausgabe" ? "bg-red-600 text-white shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
            data-testid="typ-ausgabe"><ArrowDownCircle className="w-4 h-4" /> Ausgabe</button>
        </div>

        {/* Kategorie + Datum */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Kategorie</label>
            <select value={form.kategorie} onChange={(e) => setForm({ ...form, kategorie: e.target.value })} className="w-full border rounded-sm p-2 text-sm bg-background" data-testid="input-kategorie">
              <option value="">-- Wählen --</option>
              {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Datum</label>
            <Input type="date" value={form.datum} onChange={(e) => setForm({ ...form, datum: e.target.value })} data-testid="input-datum" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Beschreibung</label>
          <Input value={form.beschreibung} onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} placeholder="z.B. Material Eiche, Werkzeug-Kauf..." data-testid="input-beschreibung" />
        </div>

        {/* Beträge */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Netto (EUR)</label>
            <Input type="number" step="0.01" value={form.betrag_netto} onChange={(e) => handleNettoChange(e.target.value)} placeholder="0.00" data-testid="input-betrag-netto" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">MwSt %</label>
            <Input type="number" step="1" value={form.mwst_satz} onChange={(e) => handleMwstChange(e.target.value)} data-testid="input-mwst-satz" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Brutto (EUR)</label>
            <Input type="number" step="0.01" value={form.betrag_brutto} onChange={(e) => handleBruttoChange(e.target.value)} placeholder="0.00" data-testid="input-betrag-brutto" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Kunde (optional)</label>
          <Input value={form.kunde} onChange={(e) => setForm({ ...form, kunde: e.target.value })} placeholder="Kundenname" data-testid="input-kunde" />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Notizen (optional)</label>
          <Textarea value={form.notizen} onChange={(e) => setForm({ ...form, notizen: e.target.value })} placeholder="Interne Notizen..." rows={2} data-testid="input-notizen" />
        </div>

        {/* Plausibilitäts-Warnungen */}
        {warnungen.length > 0 && (
          <div className="space-y-1.5 p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="plausibilitaet-warnungen">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Plausibilitätsprüfung</p>
            {warnungen.map((w, i) => (
              <p key={i} className={`text-xs flex items-center gap-1 ${w.typ === "fehler" ? "text-red-700" : w.typ === "warnung" ? "text-amber-700" : "text-blue-700"}`}>
                {w.typ === "fehler" ? <X className="w-3 h-3" /> : w.typ === "warnung" ? <AlertTriangle className="w-3 h-3" /> : <Info className="w-3 h-3" />}
                {w.text}
              </p>
            ))}
          </div>
        )}

        <div className="flex justify-between items-center pt-2 border-t">
          <button onClick={runPlausibility} disabled={checking} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="btn-plausibilitaet">
            <AlertTriangle className="w-3.5 h-3.5" /> {checking ? "Prüfe..." : "Plausibilitätsprüfung"}
          </button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-buchung">
              {saving ? "Speichern..." : isEdit ? "Aktualisieren" : "Buchung erstellen"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};


// ==================== KATEGORIEN MODAL ====================
const KategorienModal = ({ kategorien, onClose, onSaved }) => {
  const [einnahme, setEinnahme] = useState([...(kategorien.einnahme || [])]);
  const [ausgabe, setAusgabe] = useState([...(kategorien.ausgabe || [])]);
  const [newEin, setNewEin] = useState("");
  const [newAus, setNewAus] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/buchhaltung/kategorien", { einnahme: einnahme.filter((k) => k.trim()), ausgabe: ausgabe.filter((k) => k.trim()) });
      toast.success("Kategorien gespeichert");
      onSaved();
    } catch { toast.error("Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title="Buchungs-Kategorien verwalten" size="lg">
      <div className="space-y-6" data-testid="kategorien-modal">
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><ArrowUpCircle className="w-4 h-4 text-green-600" /> Einnahme-Kategorien</h3>
          <div className="space-y-1.5 mb-2">
            {einnahme.map((k, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={k} onChange={(e) => { const u = [...einnahme]; u[i] = e.target.value; setEinnahme(u); }} className="flex-1 h-8 border rounded-sm px-3 text-sm bg-white" />
                <button onClick={() => setEinnahme(einnahme.filter((_, j) => j !== i))} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={newEin} onChange={(e) => setNewEin(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && newEin.trim()) { setEinnahme([...einnahme, newEin.trim()]); setNewEin(""); } }} placeholder="Neue Kategorie..." className="flex-1 h-8 border border-dashed rounded-sm px-3 text-sm" data-testid="input-new-einnahme-kat" />
            <button onClick={() => { if (newEin.trim()) { setEinnahme([...einnahme, newEin.trim()]); setNewEin(""); } }} disabled={!newEin.trim()} className="h-8 px-3 text-xs font-medium bg-green-50 text-green-700 rounded-sm hover:bg-green-100 disabled:opacity-40"><Plus className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><ArrowDownCircle className="w-4 h-4 text-red-600" /> Ausgabe-Kategorien</h3>
          <div className="space-y-1.5 mb-2">
            {ausgabe.map((k, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={k} onChange={(e) => { const u = [...ausgabe]; u[i] = e.target.value; setAusgabe(u); }} className="flex-1 h-8 border rounded-sm px-3 text-sm bg-white" />
                <button onClick={() => setAusgabe(ausgabe.filter((_, j) => j !== i))} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={newAus} onChange={(e) => setNewAus(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && newAus.trim()) { setAusgabe([...ausgabe, newAus.trim()]); setNewAus(""); } }} placeholder="Neue Kategorie..." className="flex-1 h-8 border border-dashed rounded-sm px-3 text-sm" data-testid="input-new-ausgabe-kat" />
            <button onClick={() => { if (newAus.trim()) { setAusgabe([...ausgabe, newAus.trim()]); setNewAus(""); } }} disabled={!newAus.trim()} className="h-8 px-3 text-xs font-medium bg-red-50 text-red-700 rounded-sm hover:bg-red-100 disabled:opacity-40"><Plus className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="btn-save-kategorien">{saving ? "Speichern..." : "Kategorien speichern"}</Button>
        </div>
      </div>
    </Modal>
  );
};

export { BuchhaltungPage };
